/**
 * Schedule Instances view filters. Keep in sync with src/utils/scheduleInstanceFilters.js.
 */
import { lookAheadWindow } from '../components/Schedule/activityListFilters';

function dayKey(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

export function normalizeEmpCode(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw.toLowerCase();
  return digits.replace(/^0+/, '') || '0';
}

export function sameEmpCode(a, b) {
  const left = normalizeEmpCode(a);
  const right = normalizeEmpCode(b);
  return Boolean(left) && left === right;
}

/** Compare P6 text with an organization code or name. "TIW Western" = "tiw  western". */
export function normalizeOrgTag(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function sameOrgTag(a, b) {
  const left = normalizeOrgTag(a);
  const right = normalizeOrgTag(b);
  return Boolean(left) && left === right;
}

/**
 * Does a P6 Upd Resp value point at this viewer?
 * LGL staff match on employee code ("0054" = "54"); external users match on
 * their company's code or name from auth.organizations.
 */
export function updRespMatchesViewer(updResp, viewer = {}) {
  const value = String(updResp ?? '').trim();
  if (!value) return false;
  if (viewer.employeeCode && sameEmpCode(value, viewer.employeeCode)) return true;
  const tags = Array.isArray(viewer.orgTags) ? viewer.orgTags : [];
  return tags.some((tag) => sameOrgTag(value, tag));
}

export function isScheduleRowComplete(row) {
  if (!row) return false;
  const status = String(row.activity_status || '').toLowerCase();
  if (status.includes('complete')) return true;
  const pct = Number(row.percent_complete);
  return Number.isFinite(pct) && pct >= 100;
}

export function scheduleRowInLookAhead(row, fromDate, weeks = 3) {
  if (isScheduleRowComplete(row)) return false;
  const from = dayKey(fromDate);
  const finish = dayKey(row.finish_date);
  if (from && finish && finish < from) return true;
  if (String(row.critical || '').trim().toUpperCase() === 'Y') return true;
  const win = lookAheadWindow(fromDate, weeks);
  if (!win) return false;
  const start = dayKey(row.start_date);
  const end = finish || start;
  if (!start) return false;
  return start <= win.end && end >= win.start;
}

export { lookAheadWindow };
